// Main-thread video thumbnail generation. Decodes a video via a
// hidden <video> element, seeks to an early frame (skipping the
// usual dark camera-initialization intro), draws it to an
// OffscreenCanvas at 256x256 with center-crop, and encodes WebP.
//
// Returns `null` on any failure — unsupported codec, DRM, audio-only
// track, corrupt container, timeout, etc. — so callers can fall back
// to the file-type badge without special-casing each error.

const SUPPORTED = /^video\//i;
const SIZE = 256;

// Hard upper bound on the whole extraction. A malformed container
// can leave `loadedmetadata` pending forever; without this cap the
// upload task would never complete.
const TIMEOUT_MS = 15_000;

// Preferred seek position. We skip the very first frame because many
// camera apps record a dark initialization frame, but we clamp to
// 10% of duration so short clips still get a meaningful preview.
const TARGET_SECONDS = 1.0;
const TARGET_FRACTION = 0.1;

export function canVideoThumbnail(mime: string | undefined): boolean {
  return !!mime && SUPPORTED.test(mime);
}

export async function makeVideoThumbnail(
  file: File,
): Promise<Uint8Array | null> {
  if (!canVideoThumbnail(file.type)) return null;

  const blobUrl = URL.createObjectURL(file);
  const video = document.createElement("video");
  // `muted` + `playsInline` keep autoplay restrictions from tripping
  // even though we never call play(). `preload="auto"` asks the
  // browser to pull enough data to satisfy metadata + seek.
  video.muted = true;
  video.playsInline = true;
  video.preload = "auto";
  video.crossOrigin = "anonymous";
  video.src = blobUrl;

  try {
    const bytes = await Promise.race([
      extractFrame(video),
      timeout<null>(TIMEOUT_MS),
    ]);
    return bytes;
  } catch {
    return null;
  } finally {
    video.removeAttribute("src");
    video.load();
    URL.revokeObjectURL(blobUrl);
  }
}

async function extractFrame(
  video: HTMLVideoElement,
): Promise<Uint8Array | null> {
  await once(video, "loadedmetadata");

  const width = video.videoWidth;
  const height = video.videoHeight;
  if (width === 0 || height === 0) return null;

  const duration = Number.isFinite(video.duration) ? video.duration : 0;
  const target = Math.min(TARGET_SECONDS, Math.max(0, duration * TARGET_FRACTION));
  video.currentTime = target;
  await once(video, "seeked");

  const canvas = new OffscreenCanvas(SIZE, SIZE);
  const ctx = canvas.getContext("2d");
  if (!ctx) return null;

  const side = Math.min(width, height);
  const sx = (width - side) / 2;
  const sy = (height - side) / 2;
  ctx.drawImage(video, sx, sy, side, side, 0, 0, SIZE, SIZE);

  const blob = await canvas.convertToBlob({
    type: "image/webp",
    quality: 0.7,
  });
  const buf = await blob.arrayBuffer();
  return new Uint8Array(buf);
}

function once(el: HTMLVideoElement, event: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const onEvent = () => {
      cleanup();
      resolve();
    };
    const onError = () => {
      cleanup();
      reject(new Error(`video ${event} failed`));
    };
    const cleanup = () => {
      el.removeEventListener(event, onEvent);
      el.removeEventListener("error", onError);
    };
    el.addEventListener(event, onEvent, { once: true });
    el.addEventListener("error", onError, { once: true });
  });
}

function timeout<T>(ms: number): Promise<T> {
  return new Promise((_, reject) => {
    setTimeout(() => reject(new Error("video thumbnail timeout")), ms);
  });
}
